import React, { useState, memo } from 'react';
import { Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Helmet } from 'react-helmet-async';

const FAQSection = ({ faqs = [], title = "Frequently Asked Questions" }) => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      <Box
        component="section"
        aria-label="Frequently Asked Questions"
        sx={{ py: { xs: 8, md: 12 }, bgcolor: '#f8fafc' }}
      >
        <Container maxWidth="xl"
      sx={{
        px: { xs: 3, sm: 4, md: 6, lg: 20, xl: 20 },
        maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "1440px", xl: "1600px" },
        mx: "auto",
        textAlign: "start",
        fontFamily: "MD IO, monospace"
      }}
      >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '28px', md: '36px' },
                fontWeight: 700,
                color: '#141414',
                fontFamily: 'MD IO, monospace',
                mb: 2,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                color: '#616161',
                fontSize: { xs: '16px', md: '18px' },
                maxWidth: '560px',
                mx: 'auto',
                fontFamily: 'Segoe UI, monospace',
              }}
            >
              Everything you need to know about Sniffr.ai
            </Typography>
          </Box>

          <Box sx={{ mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={faq.id}
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`)}
                disableGutters
                elevation={0}
                sx={{
                  mb: 2,
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    borderColor: '#5998EF',
                    boxShadow: '0 0 0 1px #5998EF',
                  },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#374151' }} />}
                  sx={{
                    px: 3,
                    py: 0.5,
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                    bgcolor: expanded === `panel${index}` ? '#f0f7ff' : '#fff',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      color: '#1f2937',
                      fontFamily: 'Segoe UI, monospace',
                      pr: 2,
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, bgcolor: '#fff' }}>
                  <Typography
                    sx={{
                      color: '#6b7280',
                      fontSize: '0.9375rem',
                      lineHeight: 1.7,
                      fontFamily: 'Segoe UI, monospace',
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default memo(FAQSection);